import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { colors, borderRadius, shadows } from '../styles/designTokens';
import { PaymentHistory, PaymentHistoryParams } from '../models/CustomerModels';
import { CustomerService } from '../services/CustomerService';
import Pagination from './Pagination';

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.semantic.border.primary};
  box-shadow: ${shadows.base};
  overflow: hidden;
`;

// 테이블 스타일
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

// 테이블 헤더 스타일
const TableHeader = styled.thead`
  background: ${colors.semantic.background.secondary};
  border-bottom: 1px solid ${colors.semantic.border.primary};
`;

// 헤더 셀 스타일
const HeaderCell = styled.th`
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: ${colors.semantic.text.secondary};
  border-right: 1px solid ${colors.semantic.border.primary};
  
  &:last-child {
    border-right: none;
  }
`;

// 테이블 바디 스타일
const TableBody = styled.tbody``;

// 테이블 행 스타일
const TableRow = styled.tr`
  border-bottom: 1px solid ${colors.secondary[100]};

  &:hover {
    background: ${colors.semantic.background.secondary};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

// 데이터 셀 스타일
const DataCell = styled.td`
  padding: 16px 12px;
  font-size: 14px;
  color: ${colors.semantic.text.secondary};
  border-right: 1px solid ${colors.secondary[100]};
  
  &:last-child {
    border-right: none;
  }
`;

// 금액 스타일
const AmountText = styled.span`
  font-weight: 600;
  color: ${colors.success[600]};
`;

// 포인트 스타일
const PointText = styled.span`
  font-weight: 600;
  color: ${colors.primary[500]};
`;

// 결제수단 배지 스타일
const PaymentMethodBadge = styled.span<{ method: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${borderRadius.base};
  font-size: 12px;
  font-weight: 500;

  ${props =>
    props.method === 'CARD'
      ? `
    background: ${colors.primary[100]};
    color: ${colors.primary[800]};
  `
      : `
    background: ${colors.secondary[100]};
    color: ${colors.secondary[700]};
  `}
`;

// 빈 상태 메시지 스타일
const EmptyMessage = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${colors.semantic.text.tertiary};
  font-size: 16px;
`;

// 로딩 스피너 스타일
const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 24px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid ${colors.neutral[100]};
    border-top: 3px solid ${colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 페이지네이션 컨테이너 스타일
const PaginationContainer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.semantic.border.primary};
  background: ${colors.semantic.background.secondary};
`;

// 모바일에서만 테이블 좌우 스크롤 래퍼
const MobileTableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    table {
      min-width: 600px;
      width: 100%;
    }
    th, td {
      white-space: nowrap;
    }
  }
`;

// 결제 내역 테이블 Props 인터페이스
interface PaymentHistoryTableProps {
  userIdx: number;
}

// 결제 내역 테이블 컴포넌트
const PaymentHistoryTable: React.FC<PaymentHistoryTableProps> = ({ userIdx }) => {
  // 상태 관리
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [limit] = useState<number>(15); // 고정값으로 설정

  // 결제 내역 로드 (고객별 - 중복 호출 방지)
  const loadPaymentHistory = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params: PaymentHistoryParams = {
        userIdx,
        page,
        limit
      };

      console.log(`🔄 고객(${userIdx}) 결제 내역 로드:`, params);

      // 실제 API 호출 시도, 실패하면 목업 데이터 사용
      let response;
      try {
        response = await CustomerService.getPaymentHistory(params);
        console.log(`✅ 고객(${userIdx}) 결제 내역 API 성공:`, response);
      } catch (apiError) {
        setError('결제 내역을 불러오는데 실패했습니다.');
        console.error(`❌ 고객(${userIdx}) 결제 내역 로드 실패:`, apiError);
        return;
      }

      setPaymentHistory(response.data);
      setCurrentPage(response.page);
      setTotalPages(response.totalPages);
      setTotal(response.total);

      console.log(`✅ 고객(${userIdx}) 결제 내역 업데이트 완료:`, {
        count: response.data.length,
        page: response.page,
        total: response.total
      });
    } catch (err) {
      setError('결제 내역을 불러오는데 실패했습니다.');
      console.error(`❌ 고객(${userIdx}) 결제 내역 로드 실패:`, err);
    } finally {
      setLoading(false);
    }
  }, [userIdx, limit]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadPaymentHistory(1);
  }, [loadPaymentHistory]);

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    loadPaymentHistory(page);
  };

  // 승인일시: YYMMDDHHMMSS → yy.mm.dd HH:mm
  const formatApprovalDateTime = (dateTimeString: string) => {
    if (!dateTimeString || dateTimeString.length !== 12) {
      return dateTimeString;
    }

    const yy = dateTimeString.substring(0, 2);
    const month = dateTimeString.substring(2, 4);
    const day = dateTimeString.substring(4, 6);
    const hour = dateTimeString.substring(6, 8);
    const minute = dateTimeString.substring(8, 10);

    return `${yy}.${month}.${day} ${hour}:${minute}`;
  };

  // 숫자 포맷팅
  const formatNumber = (num: number) => {
    return num.toLocaleString('ko-KR');
  };

  // 금액 포맷팅
  const formatCurrency = (amount: number) => {
    return `${formatNumber(amount)}원`;
  };

  // 포인트 포맷팅
  const formatPoints = (points: number) => {
    return `${formatNumber(points)} P`;
  };

  // 결제수단 표시명 변환
  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'CARD':
        return '카드';
      case 'TRANSFER':
        return '계좌이체';
      default:
        return method;
    }
  };

  // 카드 정보 표시 (카드사 + 카드번호)
  const getCardInfo = (payment: PaymentHistory) => {
    if (payment.결제수단 !== 'CARD' || !payment.카드사) {
      return '-';
    }
    return `${payment.카드사} ${payment.카드번호}`;
  };

  // 로딩 상태
  if (loading) {
    return (
      <TableContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </TableContainer>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <TableContainer>
        <EmptyMessage>{error}</EmptyMessage>
      </TableContainer>
    );
  }

  // 빈 데이터 상태
  if (paymentHistory.length === 0) {
    return (
      <TableContainer>
        <EmptyMessage>결제 내역이 없습니다.</EmptyMessage>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      {/* 모바일에서만 좌우 스크롤 가능하도록 래퍼로 감쌈 */}
      <MobileTableScrollWrapper>
        <Table>
          <TableHeader>
            <tr>
              <HeaderCell>결제상품명</HeaderCell>
              <HeaderCell>결제금액</HeaderCell>
              <HeaderCell>충전포인트</HeaderCell>
              <HeaderCell>결제수단</HeaderCell>
              <HeaderCell>카드정보</HeaderCell>
              <HeaderCell>승인일시</HeaderCell>
            </tr>
          </TableHeader>
          <TableBody>
            {paymentHistory.map((payment, index) => (
              <TableRow key={index}>
                <DataCell>{payment.결제상품명}</DataCell>
                <DataCell>
                  <AmountText>{formatCurrency(payment.결제금액)}</AmountText>
                </DataCell>
                <DataCell>
                  <PointText>{formatPoints(payment.충전포인트)}</PointText>
                </DataCell>
                <DataCell>
                  <PaymentMethodBadge method={payment.결제수단}>
                    {getPaymentMethodLabel(payment.결제수단)}
                  </PaymentMethodBadge>
                </DataCell>
                <DataCell>{getCardInfo(payment)}</DataCell>
                <DataCell>{formatApprovalDateTime(payment.승인일시)}</DataCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MobileTableScrollWrapper>

      {/* 페이지네이션 - 1페이지라도 표시 */}
      <PaginationContainer>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={handlePageChange}
        />
      </PaginationContainer>
    </TableContainer>
  );
};

export default PaymentHistoryTable; 